import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderObjectData, WorkOrderSeasonData } from 'gql/graphql';
import type { Position } from 'uikit';
import { ChoiceGroup, Loader, Tooltip } from 'uikit';
import { getElementCoordinatesBySelector } from 'utils/dom';
import { runOnRefExists } from 'utils/runOnRefExists';

import type React from 'react';
import { useEffect, useRef, useCallback, useMemo, useState } from 'react';

import { ConstructionObjectCombobox } from '../ConstructionObjectCombobox';

import { MemoizedAnalyticsBySeasonChart } from './components/AnalyticsBySeasonChart';
import { MemoizedAnalyticsBySeasonsChart } from './components/AnalyticsBySeasonsChart';
import { MemoizedAnalyticsUerChart } from './components/AnalyticsUerChart';
import { TitleWithLegend } from './components/TitleWithLegend';
import { useActsKC2AnalyticsBySeasons } from './hooks/useActsKC2AnalyticsBySeasons';
import { useActsKC2AnalyticsByUer } from './hooks/useActsKC2AnalyticsByUer';

import Style from './ActsKC2Analytics.module.css';

interface IActsKC2AnalyticsByUer {
    onClose: () => void;
    workOrderObjects: WorkOrderObjectData[];
    constructionTypeId: number;
    rootObjectId?: number;
    workOrderId: number;
    workOrderSeasons: WorkOrderSeasonData[];
}

const title = 'диаграмма фактического освоения фо';
const noWorkOrderRootObjectTitle = 'Для отображения диаграммы освоения по НЗ выберите объект строительства';

const style = { width: 1280 };

let handlers: Record<string, (e: any) => any> = {};

const analyticsTypes = [
    {
        value: 'common',
        label: 'Общее',
        tooltip: 'Учитывается стоимость с начала периода по конец указанного месяца',
    },
    {
        value: 'uer',
        label: 'По расценкам',
        tooltip: 'Учитывается суммарная стоимость в разрезе расценок',
    },
    {
        value: 'seasons',
        label: 'По сезонам',
        tooltip: 'Учитывается стоимость с начала периода по конец указанного месяца',
    },
];

export const ActsKC2Analytics: React.FC<IActsKC2AnalyticsByUer> = (props) => {
    const { onClose, constructionTypeId, workOrderObjects, rootObjectId, workOrderId, workOrderSeasons } = props;

    const seasonsChoices = useMemo(
        () => workOrderSeasons?.map((s) => ({ value: s.year, label: s.year })) || [],
        [workOrderSeasons],
    );

    const choiceGroupRef = useRef<HTMLDivElement>(null);

    const [hoveredLabelMessage, setHoveredLabelMessage] = useState<string>();
    const [position, setPosition] = useState<Position>(undefined);
    const [workOrderRootObjectId, setWorkOrderRootObjectId] = useState(rootObjectId);
    const [analyticsType, setAnalyticsType] = useState(analyticsTypes[0]);
    const [selectedSeason, setSelectedSeason] = useState(seasonsChoices?.[0]);

    const { uerActsKC2AnalyticsBySeasonsData, uerActsKC2AnalyticsBySeasonsStatus } = useActsKC2AnalyticsBySeasons({
        workOrderId,
        workOrderRootObjectId,
        withEffect: ['common', 'seasons'].includes(analyticsType?.value) && !!workOrderRootObjectId,
        seasonYear: analyticsType?.value === 'seasons' ? selectedSeason?.value : undefined,
    });

    const { uerActsKC2AnalyticsByUerData, uerActsKC2AnalyticsByUerStatus } = useActsKC2AnalyticsByUer({
        workOrderId,
        workOrderRootObjectId,
        withEffect: analyticsType?.value === 'uer' && !!workOrderRootObjectId,
    });

    const handleObjectChange = useCallback(({ value }) => {
        setWorkOrderRootObjectId(value?.id);
    }, []);

    const onAnalyticsTypeChange = useCallback(({ value }) => {
        setAnalyticsType(value);
    }, []);

    const onSeasonChange = useCallback(({ value }) => {
        setSelectedSeason(value);
    }, []);

    const clearHoveredAnalyticsType = useCallback(() => {
        setHoveredLabelMessage(undefined);
        setPosition(undefined);
    }, []);

    const isLoading = useMemo(
        () =>
            uerActsKC2AnalyticsBySeasonsStatus === RequestStatus.FETCHING ||
            uerActsKC2AnalyticsByUerStatus === RequestStatus.FETCHING,
        [uerActsKC2AnalyticsBySeasonsStatus, uerActsKC2AnalyticsByUerStatus],
    );

    useEffect(() => {
        let labels: Array<Element>;

        runOnRefExists(choiceGroupRef, () => {
            labels = Array.from(choiceGroupRef?.current?.getElementsByClassName('ChoiceGroup-Label') || []);

            labels.forEach((label, index) => {
                const labelType = analyticsTypes[index];

                handlers[labelType.value] = (e: any) => {
                    const pos = getElementCoordinatesBySelector(e.target);

                    setPosition({
                        y: pos?.y || 0,
                        x: (pos?.x || 0) + (pos?.width || 0) / 2,
                    });

                    setHoveredLabelMessage(labelType.tooltip);
                };

                label.addEventListener('mouseover', handlers[labelType.value]);
                label.addEventListener('mouseout', clearHoveredAnalyticsType);
            });
        });

        return () => {
            labels?.forEach((label, index) => {
                const labelType = analyticsTypes[index];

                label.removeEventListener('mouseover', handlers[labelType.value]);
                label.removeEventListener('mouseout', clearHoveredAnalyticsType);
            });

            handlers = {};
        };
    }, [clearHoveredAnalyticsType]);

    const getObjItemDisabled = useCallback(
        (item) => {
            const currentItem = workOrderObjects?.find((i) => i.id === item.id);

            return !currentItem?.hasCompletedSkzsActsKC2;
        },
        [workOrderObjects],
    );

    return (
        <ModalDialog
            isOpen
            isOnClickOutsideDisable
            hasOverlay
            withTitleCloseButton
            withBottomButtons={false}
            title={<TitleWithLegend title={title} />}
            onClose={onClose}
            isCommit={false}
            style={style}
        >
            <>
                <div className={Style.actsKC2AnalyticsBody}>
                    <div className={Style.actsKC2AnalyticsHeader}>
                        <div className={Style.actsKC2AnalyticsHeaderObjects}>
                            <ConstructionObjectCombobox
                                constructionTypeId={constructionTypeId}
                                objectId={workOrderRootObjectId}
                                onChange={handleObjectChange}
                                workOrderObjects={workOrderObjects}
                                getItemDisabled={getObjItemDisabled}
                            />
                        </div>
                        <div>
                            <ChoiceGroup
                                value={analyticsType}
                                items={analyticsTypes}
                                onChange={onAnalyticsTypeChange}
                                getItemLabel={(item) => item?.label}
                                name="level"
                                size="s"
                                view="ghost"
                                multiple={false}
                                className={Style.actsKC2AnalyticsHeaderType}
                                ref={choiceGroupRef}
                                disabled={!workOrderRootObjectId}
                            />
                        </div>
                        {analyticsType?.value === 'seasons' && (
                            <div>
                                <ChoiceGroup
                                    value={selectedSeason}
                                    items={seasonsChoices}
                                    onChange={onSeasonChange}
                                    getItemLabel={(item) => item?.label}
                                    name="level"
                                    size="s"
                                    view="ghost"
                                    multiple={false}
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={`${Style.actsKC2AnalyticsChartBody} ${
                            !workOrderRootObjectId || isLoading ? Style.actsKC2AnalyticsChartBodyMessage : ''
                        }`}
                    >
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {!workOrderRootObjectId ? (
                            <div className={Style.actsKC2AnalyticsChartMessage}>{noWorkOrderRootObjectTitle}</div>
                        ) : isLoading ? (
                            <div className={Style.actsKC2AnalyticsChartMessage}>
                                <Loader />
                            </div>
                        ) : (
                            <>
                                {analyticsType?.value === 'uer' && (
                                    <MemoizedAnalyticsUerChart
                                        items={uerActsKC2AnalyticsByUerData?.uerActsKC2AnalyticsByUer?.items}
                                    />
                                )}
                                {analyticsType?.value === 'common' && (
                                    <MemoizedAnalyticsBySeasonsChart
                                        items={uerActsKC2AnalyticsBySeasonsData?.uerActsKC2AnalyticsBySeasons?.items}
                                        workOrderSeasons={workOrderSeasons}
                                    />
                                )}
                                {analyticsType?.value === 'seasons' && selectedSeason && (
                                    <MemoizedAnalyticsBySeasonChart
                                        items={uerActsKC2AnalyticsBySeasonsData?.uerActsKC2AnalyticsBySeasons?.items}
                                        seasonYear={selectedSeason?.value}
                                    />
                                )}
                                <div />
                            </>
                        )}
                    </div>
                </div>
                {position && (
                    <Tooltip direction="upCenter" spareDirection="downStartLeft" position={position}>
                        {hoveredLabelMessage}
                    </Tooltip>
                )}
            </>
        </ModalDialog>
    );
};

ActsKC2Analytics.displayName = 'ActsKC2Analytics';
